import { get } from "../api.js";
import { rememberedAuthTutorIdentity } from "../authIdentity.js";

export interface SchedulingTutor {
  tutorProfileId: string;
  providerId: number;
  displayName: string;
}

export interface MySchedulingIdentity {
  tutorProfileId: string | null;
  providerId: number | null;
  displayName: string | null;
}

export interface ScheduleBlockTarget {
  providerId: number;
  tutorProfileId: string;
  displayName: string;
}

/**
 * Resolve which provider a diary schedule block would be created against, using
 * the same identity surface the Time off page loads. The acting tutor's own
 * diary wins; owner / manage-all users fall back to the exact selected diary
 * tutor. Ambiguous or unknown targets return null so the caller refuses rather
 * than guessing authority.
 */
export function resolveScheduleBlockTarget(
  tutorId: string | undefined,
  canManageOthers: boolean,
  own: MySchedulingIdentity | null,
  managedTutors: readonly SchedulingTutor[],
): ScheduleBlockTarget | null {
  if (!tutorId) return null;

  if (own?.tutorProfileId === tutorId && own.providerId !== null) {
    return {
      tutorProfileId: tutorId,
      providerId: own.providerId,
      displayName: own.displayName ?? "You",
    };
  }

  if (!canManageOthers) return null;
  const selected = managedTutors.find((tutor) => tutor.tutorProfileId === tutorId);
  return selected ?? null;
}

export const scheduling = {
  // `/auth/me` is the first render gate and now carries this exact identity.
  // Reuse it instead of paying for another HTTP request and tutor-profile read.
  // Older isolated tests/mocks that omit the bootstrap field keep the existing
  // endpoint as a compatibility fallback.
  me: () => {
    const remembered = rememberedAuthTutorIdentity();
    return remembered === undefined
      ? get<MySchedulingIdentity>("/scheduling/me")
      : Promise.resolve(remembered);
  },
  tutors: async () => {
    const result = await get<{ tutors: Array<{ tutorProfileId: string; providerId: number | null; displayName: string }> }>(
      "/scheduling/tutors",
    );
    return result.tutors.filter((tutor): tutor is SchedulingTutor => tutor.providerId !== null);
  },
};
